import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from 'src/constants'

import { getLocalStorageTokens, setLocalStorageToken } from './getToken'

/**
 * Admin "impersonate member" support.
 *
 * Impersonation swaps the current admin tokens for a session minted for the
 * target member (same `Token <jwt>` scheme the app already uses). The admin's
 * own tokens are stashed under backup keys so {@link exitImpersonation} can
 * restore them. A label key drives the persistent banner.
 */
const BACKUP_ACCESS_KEY = '__imp_backup_access__'
const BACKUP_REFRESH_KEY = '__imp_backup_refresh__'
const LABEL_KEY = '__imp_label__'

/** Height (px) of the impersonation banner; the layout shell reserves this much
 *  room at the top (see `--impersonation-offset`) so nothing sits under it. */
export const IMPERSONATION_BANNER_HEIGHT = 36

export const isImpersonating = (): boolean =>
  Boolean(window.localStorage.getItem(BACKUP_ACCESS_KEY))

export const getImpersonationLabel = (): string =>
  window.localStorage.getItem(LABEL_KEY) ?? ''

/**
 * Stash the admin session, install the impersonation session, and reload as the
 * member. A full reload is intentional — it resets every in-memory query cache
 * and auth-derived state so nothing leaks across the identity switch.
 */
export const startImpersonation = (tokens: {
  access: string
  refresh: string
  name: string
}): void => {
  const current = getLocalStorageTokens()
  // Don't overwrite an existing backup (avoids losing the real admin token if
  // impersonation is somehow triggered twice).
  if (!isImpersonating() && current.access) {
    window.localStorage.setItem(BACKUP_ACCESS_KEY, current.access)
    window.localStorage.setItem(BACKUP_REFRESH_KEY, current.refresh ?? '')
  }
  window.localStorage.setItem(LABEL_KEY, tokens.name)
  setLocalStorageToken({ access: tokens.access, refresh: tokens.refresh })
  window.location.assign('/')
}

/** Restore the admin session and reload. */
export const exitImpersonation = (): void => {
  const access = window.localStorage.getItem(BACKUP_ACCESS_KEY)
  const refresh = window.localStorage.getItem(BACKUP_REFRESH_KEY)
  if (access) {
    window.localStorage.setItem(ACCESS_TOKEN_KEY, access)
    // Never restore an empty refresh: the auth interceptor treats a
    // present-but-empty refresh as "missing", clears storage and bounces to
    // /login. A cookie-auth admin had no localStorage refresh to begin with —
    // remove the key so their original (cookie-based) state is restored.
    if (refresh) {
      window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
    } else {
      window.localStorage.removeItem(REFRESH_TOKEN_KEY)
    }
  }
  window.localStorage.removeItem(BACKUP_ACCESS_KEY)
  window.localStorage.removeItem(BACKUP_REFRESH_KEY)
  window.localStorage.removeItem(LABEL_KEY)
  window.location.assign('/')
}
